import React, { useState } from 'react';
import {
  Alert,
  Button as MuiButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { AlertTriangle, Info, ExternalLink } from 'react-feather';
import { Close } from '@mui/icons-material';
import analyticsService from '~/src/services/analytics';
import { setClioExportModalDismissed } from './clioExportModalUtils';

export const MODAL_VARIANTS = {
  WARNING: 'warning',
  INFO: 'info',
} as const;

interface ClioManageExportModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  primaryActionTitle?: string;
  variant?: (typeof MODAL_VARIANTS)[keyof typeof MODAL_VARIANTS];
  alertMessage?: string;
  showCheckbox?: boolean;
  checkboxLabel?: string;
}

const ClioManageExportModal = ({
  open,
  onCancel,
  onConfirm,
  title = 'Export to Clio Manage',
  message = 'Existing document(s)/document folder(s) will be replaced in Clio Manage and a new version will be created.',
  primaryActionTitle = 'Continue to Clio Manage',
  variant = MODAL_VARIANTS.WARNING,
  alertMessage,
  showCheckbox = false,
  checkboxLabel = 'Do not show this message again',
}: ClioManageExportModalProps) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (showCheckbox && dontShowAgain) {
      setClioExportModalDismissed();
    }

    if (variant === MODAL_VARIANTS.WARNING) {
      analyticsService.track('Clio Export Warning - Confirmed Override', {
        action: 'continue_to_clio_manage',
      });
    } else {
      analyticsService.track('Clio Export Info - Confirmed', {
        action: 'continue_to_clio_manage',
      });
    }
    onConfirm();
    onCancel();
  };

  const handleCancel = () => {
    if (variant === MODAL_VARIANTS.WARNING) {
      analyticsService.track('Clio Export Warning - Cancelled Override', {
        action: 'cancel_override',
      });
    } else {
      analyticsService.track('Clio Export Info - Cancelled', {
        action: 'cancel_export',
      });
    }
    onCancel();
  };

  const isWarning = variant === MODAL_VARIANTS.WARNING;
  const IconComponent = isWarning ? AlertTriangle : Info;
  const alertSeverity = isWarning
    ? MODAL_VARIANTS.WARNING
    : MODAL_VARIANTS.INFO;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          maxWidth: '475px',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, fontSize: '16px', fontWeight: 600 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500],
            borderRadius: 24,
          }}
        >
          <Close height={20} width={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ fontSize: '14px', fontWeight: 400, p: 3 }}>
        <div style={{ marginBottom: 24 }}>{message}</div>
        {alertMessage && (
          <Alert
            sx={(theme) => ({
              padding: theme.spacing(3),
              marginBottom: theme.spacing(3),
              background: isWarning
                ? theme.palette.yellow[50]
                : theme.palette.blue[50],
              color: theme.palette.text.primary,
              '.MuiAlert-icon': {
                color: theme.palette.text.primary,
              },
              border: '1px solid',
              borderColor: isWarning
                ? theme.palette.yellow[200]
                : theme.palette.blue[200],
            })}
            icon={<IconComponent fontSize="inherit" />}
            severity={alertSeverity}
          >
            {alertMessage}
          </Alert>
        )}
        {showCheckbox && (
          <div style={{ marginTop: 24 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                style={{ marginRight: '8px' }}
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              {checkboxLabel}
            </label>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, backgroundColor: 'white', gap: 2 }}>
        <MuiButton
          onClick={handleCancel}
          sx={{
            width: 96,
            height: 40,
            borderRadius: '4px',
            borderWidth: '1px',
            paddingTop: '10px',
            paddingRight: '24px',
            paddingBottom: '10px',
            paddingLeft: '24px',
            fontSize: '14px',
          }}
        >
          Cancel
        </MuiButton>
        <MuiButton
          variant="contained"
          onClick={handleConfirm}
          endIcon={<ExternalLink size={16} />}
          sx={{
            backgroundColor: 'primary.main',
            width: 237,
            height: 40,
            borderRadius: '4px',
            borderWidth: '1px',
            paddingTop: '10px',
            paddingRight: '24px',
            paddingBottom: '10px',
            paddingLeft: '24px',
            fontSize: '14px',
          }}
        >
          {primaryActionTitle}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default ClioManageExportModal;
